import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Paper,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Storage as StorageIcon,
  ExitToApp as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Chat as ChatIcon,
  Code as CodeIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Web as WebIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { logout } from '../../store/slices/authSlice';
import { setTheme } from '../../store/slices/themeSlice';
import { fetchNotifications } from '../../store/slices/systemSlice';
import { Select, MenuItem as MuiMenuItem } from '@mui/material';
import { SidebarItem, UserRole } from '../../types';

const drawerWidth = 240;

const sidebarItems: SidebarItem[] = [
  {
    title: 'Dashboard',
    path: '/admin/dashboard',
    icon: DashboardIcon,
    allowedRoles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.USER],
  },
  {
    title: 'Users',
    path: '/admin/users',
    icon: PeopleIcon,
    allowedRoles: [UserRole.ADMIN, UserRole.MANAGER],
  },
  {
    title: 'Entities',
    path: '/admin/entities',
    icon: StorageIcon,
    allowedRoles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.USER],
  },
  {
    title: 'Data Processing',
    path: '/admin/data-processing',
    icon: RefreshIcon,
    allowedRoles: [UserRole.ADMIN, UserRole.MANAGER],
  },
  {
    title: 'Chat',
    path: '/admin/chat',
    icon: ChatIcon,
    allowedRoles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.USER],
  },
  {
    title: 'Snippet Generator',
    path: '/admin/snippets',
    icon: CodeIcon,
    allowedRoles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.USER],
  },
];

const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentTheme = useAppSelector((state) => (state as any).theme.current);
  const notifications = useAppSelector((state) => (state as any).system.notifications) as any[] | undefined;
  const unreadCount = (notifications || []).filter((n: any) => !n.read).length;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleMenuClose();
    navigate('/');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  useEffect(() => {
    dispatch(fetchNotifications());
    const intervalId = window.setInterval(() => {
      dispatch(fetchNotifications());
    }, 60000);
    return () => window.clearInterval(intervalId);
  }, [dispatch]);

  const drawer = (
    <Box sx={{ height: '100%' }}>
      <Toolbar sx={{
        backgroundColor: 'var(--color-bg)',
        borderBottom: '1px solid rgba(28, 233, 224, 0.25)'
      }}>
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            fontWeight: 'bold',
            color: 'var(--color-accent)',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
          }}
        >
          ✨ AskNow
        </Typography>
      </Toolbar>
      <Divider sx={{ borderColor: (t) => t.palette.divider }} />
      <List sx={{ pt: 2 }}>
        {sidebarItems.map((item) => {
          // Filter items based on user role
          if (item.allowedRoles && user?.role && !item.allowedRoles.includes(user.role as UserRole)) {
            return null;
          }

          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <ListItem key={item.title} disablePadding sx={{ px: 1, mb: 1 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={isActive}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(28, 233, 224, 0.12)',
                    color: 'var(--color-accent)',
                    boxShadow: '0 4px 15px rgba(28, 233, 224, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(28, 233, 224, 0.18)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 20px rgba(28, 233, 224, 0.3)',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    transform: 'translateX(4px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <ListItemIcon
                  sx={{
                    color: (t) => (isActive ? t.palette.primary.main : t.palette.text.secondary),
                    minWidth: 40,
                  }}
                >
                  <Icon />
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: isActive ? 600 : 400,
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: 'var(--color-bg)',
    }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'var(--color-bg)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(28, 233, 224, 0.25)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {sidebarItems.find(item => item.path === location.pathname)?.title || 'AskNow'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isMobile && (
              <Select
                size="small"
                value={currentTheme}
                onChange={(e) => dispatch(setTheme(e.target.value as any))}
                sx={{
                  mr: 2,
                  minWidth: 120,
                  color: 'inherit',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                  '.MuiSvgIcon-root': { color: 'inherit' }
                }}
              >
                <MuiMenuItem value={'dark'}>Dark</MuiMenuItem>
                <MuiMenuItem value={'ocean'}>Ocean</MuiMenuItem>
              </Select>
            )}
            <IconButton color="inherit" onClick={() => handleNavigation('/admin/notifications')}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <Tooltip title={user?.username || 'Account'}>
              <IconButton
                onClick={handleMenuOpen}
                color="inherit"
                sx={{ ml: 1 }}
              >
                <Avatar sx={{
                  width: 32,
                  height: 32,
                  background: (t) => `linear-gradient(118deg, ${t.palette.secondary.main}55, ${t.palette.primary.main})`,
                  boxShadow: (t) => `0 6px 18px ${t.palette.primary.main}55`,
                  color: 'inherit'
                }}>
                  {user?.username?.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: 'var(--color-bg)',
              color: 'white',
              borderRight: '1px solid rgba(28, 233, 224, 0.25)',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: 'var(--color-bg)',
              color: 'white',
              borderRight: '1px solid rgba(28, 233, 224, 0.25)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, md: 8 },
          background: 'transparent',
          minHeight: 'calc(100vh - 56px)',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 3,
            border: '1px solid rgba(28, 233, 224, 0.25)',
            minHeight: { xs: 'auto', md: 'calc(100vh - 120px)' },
            p: { xs: 1.5, md: 3 },
          }}
        >
          <Outlet />
        </Paper>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 200,
            overflow: 'visible',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
            {user?.username || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ textTransform: 'capitalize' }}>
            {user?.role || 'Role'}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => navigate('/admin/profile')}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => navigate('/admin/settings')}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
